import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'

import { ApolloQueryResult } from '@apollo/client'
import { InitialDataLoaderCartQuery } from '../graphql'
import calculateCheckoutSteps from '../lib/calculate-checkout-steps'
import Context, { ContextData } from './context'
import Routes from './routes'
import useActionHandlers from './useActionHandlers'

interface InitialState {
  initialSteps: Array<string>
  isVenueBrandingEnabled: boolean
}

interface Props {
  initialState: InitialState
  refetchCart: () => Promise<ApolloQueryResult<InitialDataLoaderCartQuery>>
}

const CheckoutContext: React.FC<React.PropsWithChildren<Props>> = ({ initialState, refetchCart, children }) => {
  const { initialSteps, isVenueBrandingEnabled } = initialState

  const location = useLocation()
  const [steps, setSteps] = useState(initialSteps)
  const [hasMutationNetworkError, setHasMutationNetworkError] = useState<boolean>()
  const [isSummaryLoading, setIsSummaryLoading] = useState(false)

  const recalculateSteps = async (): Promise<void> => {
    const { data } = await refetchCart()

    if (data?.cart) {
      setSteps(calculateCheckoutSteps(data.cart))
    }
  }

  const getNextStep = (): string => {
    const activeStep = location.pathname
    const activeStepIndex = steps.indexOf(activeStep)

    if (activeStepIndex === steps.length - 1) return activeStep

    const nextStep = steps[activeStepIndex + 1]
    return nextStep
  }

  const getPreviousStep = (): string => {
    const activeStep = location.pathname
    const activeStepIndex = steps.indexOf(activeStep)

    if (activeStepIndex === 0) return activeStep

    const previousStep = steps[activeStepIndex - 1]
    return previousStep
  }

  // Step validation flags
  const [isProductSelectionValid, setIsProductSelectionValid] = useState(false)
  const [isRequiredInformationValid, setIsRequiredInformationValid] = useState(false)
  const [isAddOnsValid, setIsAddOnsValid] = useState(false)
  const [isPoliciesValid, setIsPoliciesValid] = useState(false)
  const [isMultiAnswerPatronQuestionValid, setIsMultiAnswerPatronQuestionValid] = useState(false)
  const [isPaymentValid, setIsPaymentValid] = useState(false)

  const isCurrentStepValid = (step: Routes): boolean => {
    switch (step) {
      case Routes.CHOOSE_PRODUCT:
        return isProductSelectionValid
      case Routes.REVIEW_INFORMATION:
        return true
      case Routes.ADDITIONAL_INFORMATION:
        return isRequiredInformationValid && isAddOnsValid && isPoliciesValid && isMultiAnswerPatronQuestionValid
      case Routes.PAYMENT:
        return isPaymentValid
      case Routes.CONFIRMATION:
        return true
      default:
        return false
    }
  }

  // Handle registered actions
  const [registerVoucherRemovalHandler, onVoucherRemoval] = useActionHandlers<{ code: string }>()

  // Payment processing
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false)
  const [processPayment, setProcessPayment] = useState()

  // Global expected errors handling
  const [errors, setErrors] = useState<Record<string, string>>({})

  const setError = (key: string, error: string) => {
    setErrors({ ...errors, [key]: error })
  }
  const getError = (key: string) => errors[key] || null
  const clearError = (key: string) => delete errors[key]

  const getErrors = () => Object.values(errors)
  const clearErrors = () => setErrors({})

  const value: ContextData = {
    steps,
    setSteps,
    recalculateSteps,
    getNextStep,
    getPreviousStep,
    hasMutationNetworkError,
    setHasMutationNetworkError,
    isProductSelectionValid,
    setIsProductSelectionValid,
    isRequiredInformationValid,
    setIsRequiredInformationValid,
    isAddOnsValid,
    setIsAddOnsValid,
    isPoliciesValid,
    setIsPoliciesValid,
    setIsMultiAnswerPatronQuestionValid,
    isPaymentValid,
    setIsPaymentValid,
    isCurrentStepValid,
    onVoucherRemoval,
    registerVoucherRemovalHandler,
    isPaymentProcessing,
    setIsPaymentProcessing,
    processPayment,
    // @ts-ignore Incorrect type on state asserted
    setProcessPayment,
    errors,
    setError,
    getError,
    clearError,
    getErrors,
    clearErrors,
    isSummaryLoading,
    setIsSummaryLoading,
    isVenueBrandingEnabled,
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export default CheckoutContext
